import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { CourseProgress, LessonProgress, StepProgress } from '@/types/lesson';
import { createStorage, DB_NAME, STORE_NAMES } from '@/lib/storage';
import { getTotalLessonsCount } from '@/data/index';

/** Текущая версия схемы данных */
const CURRENT_VERSION = 2;

/** Ключ для хранения */
const STORAGE_KEY = 'git-lessons-progress';

/** Хранилище для прогресса (IndexedDB с fallback на localStorage) */
const progressStorage: StateStorage = createStorage({
  dbName: DB_NAME,
  storeName: STORE_NAMES.PROGRESS,
});

/** Начальное состояние прогресса */
const initialProgress: CourseProgress = {
  version: CURRENT_VERSION,
  currentLessonId: '1-1',
  lessons: {},
  overallProgress: 0,
  lastActivityAt: Date.now(),
  settings: {
    theme: 'dark',
    showHints: true,
    autoAdvance: true,
  },
};

/** Экспортируемый тип прогресса для JSON */
export interface ExportedProgress {
  version: number;
  exportedAt: number;
  progress: CourseProgress;
  gitState?: string;
}

interface LessonStore {
  // State
  progress: CourseProgress;

  // Actions
  startLesson: (lessonId: string) => void;
  completeStep: (lessonId: string, stepId: number, totalSteps?: number) => void;
  completeLesson: (lessonId: string) => void;
  setCurrentStep: (lessonId: string, stepId: number) => void;
  useHint: (lessonId: string, stepId: number) => void;
  recordAttempt: (lessonId: string, stepId: number) => void;
  resetLesson: (lessonId: string) => void;
  resetAllProgress: () => void;
  saveGitState: (lessonId: string, state: string) => void;
  updateSettings: (settings: Partial<CourseProgress['settings']>) => void;

  // Import/Export
  exportProgress: (gitState?: string) => ExportedProgress;
  importProgress: (data: ExportedProgress) => boolean;

  // Getters
  getLessonProgress: (lessonId: string) => LessonProgress | undefined;
  getStepProgress: (lessonId: string, stepId: number) => StepProgress | undefined;
  isLessonCompleted: (lessonId: string) => boolean;
  isStepCompleted: (lessonId: string, stepId: number) => boolean;
  getCompletedLessonsCount: () => number;
  getOverallProgress: () => number;
}

/**
 * Zustand store для прогресса обучения
 */
export const useLessonStore = create<LessonStore>()(
  persist(
    (set, get) => ({
      progress: initialProgress,

      startLesson: (lessonId) => {
        set((state) => {
          const existingProgress = state.progress.lessons[lessonId];

          return {
            progress: {
              ...state.progress,
              currentLessonId: lessonId,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: existingProgress ?? {
                  lessonId,
                  started: true,
                  startedAt: Date.now(),
                  completed: false,
                  steps: {},
                  currentStep: 1,
                },
              },
            },
          };
        });
      },

      completeStep: (lessonId, stepId) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          const updatedSteps: Record<number, StepProgress> = {
            ...lessonProgress.steps,
            [stepId]: {
              stepId,
              completed: true,
              completedAt: Date.now(),
              attempts: lessonProgress.steps[stepId]?.attempts ?? 1,
              hintsUsed: lessonProgress.steps[stepId]?.hintsUsed ?? 0,
            },
          };

          // Проверяем, завершён ли урок (все шаги выполнены)
          // Это будет определено внешне на основе количества шагов в уроке

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: {
                  ...lessonProgress,
                  steps: updatedSteps,
                  currentStep: stepId + 1,
                },
              },
            },
          };
        });
      },

      setCurrentStep: (lessonId, stepId) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: {
                  ...lessonProgress,
                  currentStep: stepId,
                },
              },
            },
          };
        });
      },

      useHint: (lessonId, stepId) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          const currentStepProgress = lessonProgress.steps[stepId] ?? {
            stepId,
            completed: false,
            attempts: 0,
            hintsUsed: 0,
          };

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: {
                  ...lessonProgress,
                  steps: {
                    ...lessonProgress.steps,
                    [stepId]: {
                      ...currentStepProgress,
                      hintsUsed: currentStepProgress.hintsUsed + 1,
                    },
                  },
                },
              },
            },
          };
        });
      },

      recordAttempt: (lessonId, stepId) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          const currentStepProgress = lessonProgress.steps[stepId] ?? {
            stepId,
            completed: false,
            attempts: 0,
            hintsUsed: 0,
          };

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: {
                  ...lessonProgress,
                  steps: {
                    ...lessonProgress.steps,
                    [stepId]: {
                      ...currentStepProgress,
                      attempts: currentStepProgress.attempts + 1,
                    },
                  },
                },
              },
            },
          };
        });
      },

      resetLesson: (lessonId) => {
        set((state) => {
          const { [lessonId]: _, ...remainingLessons } = state.progress.lessons;

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: remainingLessons,
            },
          };
        });
      },

      saveGitState: (lessonId, gitState) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: {
                ...state.progress.lessons,
                [lessonId]: {
                  ...lessonProgress,
                  gitState,
                },
              },
            },
          };
        });
      },

      updateSettings: (settings) => {
        set((state) => ({
          progress: {
            ...state.progress,
            settings: {
              ...state.progress.settings,
              ...settings,
            },
          },
        }));
      },

      getLessonProgress: (lessonId) => {
        return get().progress.lessons[lessonId];
      },

      getStepProgress: (lessonId, stepId) => {
        const lessonProgress = get().progress.lessons[lessonId];
        return lessonProgress?.steps[stepId];
      },

      isLessonCompleted: (lessonId) => {
        const lessonProgress = get().progress.lessons[lessonId];
        return lessonProgress?.completed ?? false;
      },

      isStepCompleted: (lessonId, stepId) => {
        const lessonProgress = get().progress.lessons[lessonId];
        return lessonProgress?.steps[stepId]?.completed ?? false;
      },

      getCompletedLessonsCount: () => {
        const lessons = get().progress.lessons;
        return Object.values(lessons).filter((l) => l.completed).length;
      },

      completeLesson: (lessonId) => {
        set((state) => {
          const lessonProgress = state.progress.lessons[lessonId];
          if (!lessonProgress) return state;

          const updatedLessons = {
            ...state.progress.lessons,
            [lessonId]: {
              ...lessonProgress,
              completed: true,
              completedAt: Date.now(),
            },
          };

          // Подсчитываем общий прогресс динамически
          const completedCount = Object.values(updatedLessons).filter((l) => l.completed).length;
          const totalLessons = getTotalLessonsCount(); // Динамическое получение количества уроков
          const overallProgress = Math.round((completedCount / totalLessons) * 100);

          return {
            progress: {
              ...state.progress,
              lastActivityAt: Date.now(),
              lessons: updatedLessons,
              overallProgress,
            },
          };
        });
      },

      resetAllProgress: () => {
        set({
          progress: {
            ...initialProgress,
            lastActivityAt: Date.now(),
          },
        });
      },

      exportProgress: (gitState) => {
        const { progress } = get();
        return {
          version: CURRENT_VERSION,
          exportedAt: Date.now(),
          progress,
          gitState: gitState ?? undefined,
        };
      },

      importProgress: (data) => {
        try {
          // Валидация структуры
          if (!data.version || !data.progress) {
            console.error('Invalid progress data structure');
            return false;
          }

          // Миграция данных при необходимости
          let migratedProgress = data.progress;
          if (data.version < CURRENT_VERSION) {
            migratedProgress = migrateProgress(data.progress, data.version);
          }

          set({
            progress: {
              ...migratedProgress,
              lastActivityAt: Date.now(),
            },
          });

          return true;
        } catch (error) {
          console.error('Failed to import progress:', error);
          return false;
        }
      },

      getOverallProgress: () => {
        return get().progress.overallProgress;
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => progressStorage),
      version: CURRENT_VERSION,
      // Миграция при загрузке
      migrate: (persistedState, version) => {
        const state = persistedState as { progress: CourseProgress };
        if (version < CURRENT_VERSION) {
          return {
            ...state,
            progress: migrateProgress(state.progress, version),
          };
        }
        return state;
      },
    }
  )
);

/**
 * Миграция данных прогресса при изменении версии схемы
 */
function migrateProgress(progress: CourseProgress, fromVersion: number): CourseProgress {
  let migrated = { ...progress };

  // Пример миграции с версии 0 на версию 1
  if (fromVersion < 1) {
    migrated = {
      ...migrated,
      version: 1,
      settings: migrated.settings ?? initialProgress.settings,
    };
  }

  // Добавить будущие миграции здесь
  // if (fromVersion < 2) { ... }

  return migrated;
}
